import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ExclamationIcon } from '@/components/icons/exclamation';
import { Input } from '@/components/ui/input';
import { TiptapEditor } from '@/components/ui/tiptap-editor';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBaseWorkerAttributes } from '@/hooks/use-base-worker-attributes';
import { normalizeHtmlContent } from '@/utils/string-util';

interface MessageContentProps {
  openingId: string;
}

export function MessageContent({ openingId }: MessageContentProps) {
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = hookForm;

  const campaignType = watch(`pool.campaignType`);
  const isEmailCampaign = campaignType === 'email';

  const subjectError =
    errors?.pool?.openingMessages?.[openingId]?.subject?.message;
  const bodyError = errors?.pool?.openingMessages?.[openingId]?.body?.message;

  // Get base worker attributes (hardcoded defaults)
  // TODO: Add API integration later to fetch custom attributes
  const baseAttributes = useBaseWorkerAttributes();

  return (
    <div className="relative flex flex-1 flex-col gap-3 overflow-y-auto py-5 px-6">
      {/* Description */}
      <Typography variant="body1" className="!text-fx-neutral-400">
        <FormattedMessage
          defaultMessage="Sends when campaign is activated"
          description="message content description"
        />
      </Typography>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Subject */}
      {isEmailCampaign && (
        <>
          <div className="-mt-2 flex items-center gap-4">
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Subject"
                description="Subject of the message content"
              />
            </Typography>
            <Input
              {...register(`pool.openingMessages.${openingId}.subject`)}
              placeholder={intl.formatMessage({
                defaultMessage: 'Type your subject',
                description: 'Placeholder for the subject input',
              })}
              className="w-full"
              isBorderless
            />
            {subjectError && <RequiredError />}
          </div>
          <div className="bg-fx-neutral-200 -mt-2 h-px w-full" />
        </>
      )}

      <Controller
        name={`pool.openingMessages.${openingId}.body`}
        control={control}
        render={({ field }) => (
          <TiptapEditor
            appearance="borderless"
            height="full"
            preset={isEmailCampaign ? 'full' : undefined}
            features={isEmailCampaign ? undefined : []}
            content={field.value ?? ''}
            onChange={(html: string) => {
              // Normalize empty HTML content to null to properly clear form state
              const normalizedValue = normalizeHtmlContent(html);
              field.onChange(normalizedValue);
            }}
            placeholder={intl.formatMessage({
              defaultMessage: 'Type your message...',
              description: 'Placeholder for the job description editor',
            })}
            workerAttributes={baseAttributes}
          />
        )}
      />

      {bodyError && (
        <div className="absolute bottom-0 right-6">
          <RequiredError />
        </div>
      )}
    </div>
  );
}

function RequiredError() {
  return (
    <div className="flex items-center gap-1">
      <ExclamationIcon />
      <Typography variant="small1" className="!text-fx-rose">
        <FormattedMessage
          defaultMessage="Required"
          description="Input is required"
        />
      </Typography>
    </div>
  );
}
